import { useEffect, useState } from 'react'
import { API_URL, fetchOptions } from 'store/fetchUtils'

const COMPLETE_POSTAL_CODE = /^\d{4}-\d{3}$/

/**
 * Checks a postal code against the backend regional/central rules.
 *
 * Runs for every segment with a complete postal code (the regional / central
 * automation applies to all branches; only the Trust AI suggestion is AT-only).
 * Backend response shape: { regional, originCoordinates, centralWithin25km }.
 *
 * Returns the flags the initial form uses to decide whether to force the
 * default forwarding values (central + ITA = true) and to hide questions 3/4:
 *  - postalForcesDefaults: postal is regional OR a central provider is <= 25km
 *  - postalCodeValidated: the check finished (used to reveal Q3/Q4)
 *  - postalMissingCoordinates: postal has no coordinates (warn the operator)
 *  - isCheckingPostal: request in flight (show a loading indicator)
 */
export const usePostalRegionalCheck = ({
	postalCode,
	insuranceId,
	requisitionStateId,
	contactLocked = false,
}) => {
	const [postalForcesDefaults, setPostalForcesDefaults] = useState(false)
	const [postalIsRegional, setPostalIsRegional] = useState(false)
	const [postalCentralWithin25km, setPostalCentralWithin25km] =
		useState(false)
	const [postalCodeValidated, setPostalCodeValidated] = useState(false)
	const [postalMissingCoordinates, setPostalMissingCoordinates] =
		useState(false)
	const [isCheckingPostal, setIsCheckingPostal] = useState(false)

	useEffect(() => {
		setPostalForcesDefaults(false)
		setPostalIsRegional(false)
		setPostalCentralWithin25km(false)
		setPostalCodeValidated(false)
		setPostalMissingCoordinates(false)
		setIsCheckingPostal(false)

		// After the victim is contacted the initial assessment is frozen.
		// Reset above must run first, otherwise contactLocked flipping to
		// true while a check is in flight leaves isCheckingPostal stuck at
		// true (the in-flight request's own cleanup skips it via `cancelled`).
		if (contactLocked) {
			return
		}

		const isComplete = COMPLETE_POSTAL_CODE.test(postalCode ?? '')
		if (requisitionStateId === 2 || !isComplete) {
			return
		}

		let cancelled = false
		setIsCheckingPostal(true)
		const checkRegional = async () => {
			try {
				const response = await fetch(
					`${API_URL}/api/suggest/processes/regional/${postalCode}?insuranceId=${insuranceId}`,
					fetchOptions({})
				)
				if (!response.ok) throw new Error('Network response was not ok')
				// Force defaults if regional OR a central provider is <= 25km.
				const result = await response.json()
				if (!cancelled) {
					// No coordinates -> warn the operator (can't check central).
					setPostalMissingCoordinates(!result?.originCoordinates)
					setPostalIsRegional(result?.regional === true)
					setPostalCentralWithin25km(
						result?.centralWithin25km === true
					)
					setPostalForcesDefaults(
						result?.regional === true ||
							result?.centralWithin25km === true
					)
				}
			} catch (error) {
				console.error('Error checking regional postal code:', error)
				if (!cancelled) {
					setPostalForcesDefaults(false)
					setPostalIsRegional(false)
					setPostalCentralWithin25km(false)
				}
			} finally {
				if (!cancelled) {
					setPostalCodeValidated(true)
					setIsCheckingPostal(false)
				}
			}
		}
		checkRegional()

		return () => {
			cancelled = true
		}
		// eslint-disable-next-line
	}, [postalCode, requisitionStateId, contactLocked])

	return {
		postalForcesDefaults,
		postalIsRegional,
		postalCentralWithin25km,
		postalCodeValidated,
		postalMissingCoordinates,
		isCheckingPostal,
	}
}
